/**
 * Privacy-first analytics adapter.
 * Auto-detects Plausible or PostHog via env vars — no-op if neither is configured.
 *
 * Env vars (frontend/.env):
 *   REACT_APP_PLAUSIBLE_DOMAIN  → enables Plausible (e.g. "visionai.studio")
 *   REACT_APP_POSTHOG_KEY       → enables PostHog (project API key)
 *   REACT_APP_POSTHOG_HOST      → optional PostHog host override
 *
 * Usage:
 *   import { track, initAnalytics } from "@/lib/analytics";
 *   initAnalytics(); // in App.js once
 *   track("signup", { plan: "free" });
 */

const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;

export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // Plausible: single script tag, DNT-honoring, no cookies by default
  if (PLAUSIBLE_DOMAIN) {
    const s = document.createElement("script");
    s.defer = true;
    s.setAttribute("data-domain", PLAUSIBLE_DOMAIN);
    s.src = "https://plausible.io/js/script.js";
    document.head.appendChild(s);
    window.plausible = window.plausible || function () {
      (window.plausible.q = window.plausible.q || []).push(arguments);
    };
  }

  // PostHog: dynamic import to keep bundle small when not configured
  if (POSTHOG_KEY) {
    import("posthog-js").then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: "identified_only",
        capture_pageview: true,
      });
      window.__posthog = posthog;
    }).catch(() => {});
  }
}

export function track(event, props = {}) {
  if (typeof window === "undefined") return;
  try {
    if (PLAUSIBLE_DOMAIN && window.plausible) {
      window.plausible(event, { props });
    }
    if (POSTHOG_KEY && window.__posthog) {
      window.__posthog.capture(event, props);
    }
  } catch { /* no-op */ }
}

export function identify(userId, traits = {}) {
  if (typeof window === "undefined") return;
  try {
    if (POSTHOG_KEY && window.__posthog) {
      window.__posthog.identify(userId, traits);
    }
  } catch { /* no-op */ }
}

export function reset() {
  try {
    if (window.__posthog) window.__posthog.reset();
  } catch { /* no-op */ }
}
